import { FeedbackIcon } from "@/components/feedback"
import { checkoutConfirmation } from "@/lib/checkout-outcome"
import { ButtonLink, Heading, PageContainer, SurfaceProvider, Text } from "@/components/ui"
import { SEASONAL_SCENES, type ScenePhase } from "@/components/seasonal"
import { DURATION, SPRING, STAGGER, TRANSITION } from "@/lib/motion"
import { motion } from "motion/react"
import { useEffect, useState } from "react"
import { useLocation, useParams } from "react-router-dom"
import { cancelAbandonedCartTimer } from "@/domain/marketing/abandoned-cart"
import { getMerchantId } from "@/lib/company-storage"
import { useSeasonalEffects } from "@/hooks/use-seasonal-effects"
import { playEffectSound } from "@/lib/effect-sound"
import { celebrate } from "./lib/celebration"

/** Navigation payload the review step hands this page after a confirmed order. */
export type OrderConfirmedState = {
  /** The chosen slot in words; absent for an immediate order. */
  scheduledSummary?: string | null
}

function readScheduledSummary(state: unknown): string | null {
  if (typeof state !== "object" || state === null || !("scheduledSummary" in state)) {
    return null
  }
  const summary = (state as OrderConfirmedState).scheduledSummary
  return typeof summary === "string" && summary.length > 0 ? summary : null
}

export function OrderConfirmedPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const { state } = useLocation()
  const confirmation = checkoutConfirmation(readScheduledSummary(state))
  const seasonal = useSeasonalEffects("orderConfirmed")
  const celebration = seasonal?.celebration ?? "confetti"
  const sound = seasonal?.sound
  const envelope = seasonal?.soundEnvelope
  const Scene = seasonal?.scene ? SEASONAL_SCENES[seasonal.scene] : undefined
  const [phase, setPhase] = useState<ScenePhase>("day")
  const nightfall = !Scene || phase === "night"
  const dark = Scene !== undefined && phase !== "day"

  useEffect(() => {
    const merchantId = getMerchantId()
    if (merchantId) cancelAbandonedCartTimer(merchantId)
  }, [])

  useEffect(() => {
    if (!nightfall) return
    if (!sound) {
      celebrate(celebration)
      return
    }
    return playEffectSound(sound, { onStart: () => celebrate(celebration, { envelope }) })
  }, [nightfall, celebration, sound, envelope])

  return (
    <PageContainer as="main" gutter="inset" className="flex min-h-fill flex-col items-center justify-center">
      {Scene && <Scene onPhaseChange={setPhase} />}
      <SurfaceProvider surface={dark ? "night" : "page"}>
        <div className="night-scene-ink relative flex w-full max-w-md flex-col items-center gap-6 py-10 text-center">
          <div className="relative h-32 flex items-center justify-center">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ ...SPRING.bounce, delay: STAGGER.tight }}
            >
              <FeedbackIcon variant="success" size="lg" tintBg="bg-secondary" accentText="text-on-media" />
            </motion.div>
          </div>

          <motion.div
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ ...TRANSITION.enter, delay: DURATION.moderate }}
            className="space-y-2"
          >
            <Heading level={1} size="display" leading="tight">
              {confirmation.title}
            </Heading>
            <Text size="base" tone="muted">
              {confirmation.message}
            </Text>
          </motion.div>

          <motion.div
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ ...TRANSITION.enter, delay: DURATION.entrance }}
            className="w-full flex flex-col gap-3 mt-4"
          >
            {orderId ? (
              <ButtonLink size="lg" to={`/profile/orders/${orderId}`} replace fullWidth {...(dark && { tone: "night" as const })}>
                Acompanhar pedido
              </ButtonLink>
            ) : null}
            <ButtonLink
              variant="outline"
              size="lg"
              to="/"
              replace
              fullWidth
              {...(dark ? { tone: "night" as const } : { tone: "neutral" as const })}
            >
              Voltar ao início
            </ButtonLink>
          </motion.div>
        </div>
      </SurfaceProvider>
    </PageContainer>
  )
}
